/**
 * The sound of a pack going off. **four cues, each saying one thing**.
 *
 * 1. **The charge**. Pressure building behind the seal.
 * 2. **The burst**. It lets go.
 * 3. **The cards**. What comes out.
 * 4. **The foil**. And, when the pack earned it, one bright note on top.
 *
 * There is no bespoke recording for any of this. What there is: thunder, three
 * magic stings, a chest and a scry. A burst is not one of those played louder,
 * it is several of them at once doing different jobs. One pitched down for
 * weight, one pitched up and stripped of bass for the crack, one for sparkle,
 * one for the reward. A few milliseconds of offset between them is what stops
 * the stack reading as a single clipped sample.
 */

import { UI, playVoice } from './uiSounds';

/**
 * Pressure building. A low bed that swells under everything, and a riser whose
 * gaps close as it goes. Evenly spaced ticks read as a countdown, closing ones
 * read as something about to give.
 *
 * @param seconds  how long until the burst.
 */
export function chargeUp(seconds = 0.62) {
  playVoice(UI.MAGIC_LOW, {
    volume: 1.0,
    rate: 0.45,
    lowpass: 520,
    attack: seconds * 0.8,
    decay: 1.2,
  });

  const TICKS = 7;
  for (let i = 0; i < TICKS; i++) {
    const t = i / (TICKS - 1);
    playVoice(UI.SCRY, {
      // 1-(1-t)^2 lands the ticks ever closer together toward the end.
      when: seconds * (1 - (1 - t) ** 2),
      volume: 0.22 + 0.5 * t,
      rate: 1.2 + t * 1.6,
      highpass: 800,
      decay: 0.18,
    });
  }
}

/**
 * It lets go. Weight first, then the crack over the top, then sparkle, then the
 * chest. The last two late enough to be heard as their own events rather than
 * as part of the impact.
 *
 * The body layers are held and released rather than decayed. The first version
 * used `decay` and measured -37.5dB RMS. Identical to the music bed, so the
 * biggest moment on the screen arrived at background level.
 *
 * Measured over 6s at the default fader: -5.7dB peak, -24.9dB RMS, and audible
 * for 3.8s. Dry and without the room it stopped at 1.9s, which is the tell.
 * thunder that ends when its file ends did not happen anywhere.
 *
 * Peak is held near -6dB on purpose: at a maxed fader that is about -1dB, so
 * the stacked layers plus the reverb return still do not clip. Pushing the
 * body to 0.95 and the room to 0.6 measured +0.2dB there, which does.
 */
export function burst() {
  // The body rolls away rather than stopping: a shorter hold than before, a
  // much longer release, and half of it sent to the room so the tail carries
  // on after the sample itself has been cut. Thunder that ends when its file
  // ends is the giveaway that it came out of a computer.
  playVoice(UI.THUNDER, {
    volume: 0.85, rate: 0.7, lowpass: 900, hold: 1.3, release: 1.9, reverb: 0.55,
  });
  // The others get a little of the same room, or the boom sounds like it is
  // somewhere the rest of the burst is not.
  playVoice(UI.THUNDER_ALT, {
    volume: 0.5, rate: 1.6, highpass: 1200, decay: 0.45, when: 0.012, reverb: 0.3,
  });
  playVoice(UI.MAGIC_ALT, {
    volume: 0.55, rate: 1.12, highpass: 600, hold: 0.7, release: 0.6, when: 0.022, reverb: 0.25,
  });
  playVoice(UI.CHEST_OPEN, { volume: 0.5, when: 0.05, reverb: 0.15 });
}

/** What comes out: air, then the cards themselves. */
export function cardsOut() {
  playVoice(UI.WHOOSH, { volume: 0.9, rate: 1.15, hold: 0.35, release: 0.4 });
  playVoice(UI.CARD_SHOVE, { volume: 0.85, rate: 0.95, when: 0.07 });
}

/** One bright note, only when the pack actually held a foil. The source is the
 *  quietest file in the library at -36dB, so it needs most of the fader. */
export function foilChime() {
  playVoice(UI.FOIL_REVEAL, { volume: 0.9, when: 0.12 });
}
